import { Frame, Heading, MousePointerClick, Quote, SquareStack, TrendingUp } from 'lucide-react';

import { AdComponent, base, margin, shape, text, unit } from './shared';

/** Pierwszy komplet klocków: CTA, nagłówek, karta, cytat, metryka, ramka. */
export const CORE_COMPONENTS: AdComponent[] = [

  {
    id: 'cta',
    label: 'Przycisk CTA',
    description: 'Kapsułka z napisem i strzałką',
    icon: MousePointerClick,
    build: ({ scene, palette, fonts }) => {
      const u = unit(scene);
      const fontSize = Math.round(u * 0.032);
      const width = Math.round(scene.width * 0.5);
      const x = margin(scene);
      const y = Math.round(scene.height * 0.76);
      const height = Math.round(fontSize * 2.6);

      return [
        text('Przycisk CTA', {
          text: 'Umów prezentację',
          x,
          y,
          width,
          fontSize,
          fontFamily: fonts.badge,
          fontWeight: 800,
          uppercase: true,
          align: 'center',
          color: palette.dark,
          background: palette.accent,
          backgroundRadius: 999,
          padding: Math.round(fontSize * 0.8),
        }),
        shape('Strzałka', {
          shape: 'triangle',
          x: x + width + Math.round(u * 0.03),
          y: y + Math.round(height * 0.28),
          width: Math.round(u * 0.035),
          height: Math.round(u * 0.035),
          rotation: 90,
          fill: palette.accent,
        }),
      ];
    },
  },

  {
    id: 'headline',
    label: 'Nagłówek z akcentem',
    description: 'Napis i belka pod nim',
    icon: Heading,
    build: ({ scene, palette, fonts }) => {
      const u = unit(scene);
      const x = margin(scene);
      const width = scene.width - x * 2;
      const y = Math.round(scene.height * 0.24);
      const fontSize = Math.round(u * 0.085);

      return [
        text('Nagłówek', {
          text: 'Gość zamawia z pokoju',
          x,
          y,
          width,
          fontSize,
          fontFamily: fonts.headline,
          fontWeight: 900,
          lineHeight: 1.08,
          color: palette.light,
        }),
        shape('Belka akcentu', {
          x,
          y: y + Math.round(fontSize * 2.6),
          width: Math.round(width * 0.18),
          height: Math.max(4, Math.round(u * 0.009)),
          fill: palette.accent,
          radius: 999,
        }),
      ];
    },
  },

  {
    id: 'card',
    label: 'Karta na tekst',
    description: 'Półprzezroczysty blok pod napis na zdjęciu',
    icon: SquareStack,
    build: ({ scene, palette, fonts }) => {
      const u = unit(scene);
      const x = margin(scene);
      const width = scene.width - x * 2;
      const y = Math.round(scene.height * 0.55);
      const height = Math.round(scene.height * 0.3);
      const inset = Math.round(u * 0.05);

      return [
        shape('Tło karty', {
          x,
          y,
          width,
          height,
          fill: palette.dark,
          opacity: 0.82,
          radius: Math.round(u * 0.03),
        }),
        text('Tytuł karty', {
          text: 'Mniej telefonów na recepcji',
          x: x + inset,
          y: y + inset,
          width: width - inset * 2,
          fontSize: Math.round(u * 0.05),
          fontFamily: fonts.subtitle,
          fontWeight: 800,
          color: palette.light,
        }),
        text('Opis karty', {
          text: 'Zamówienia z pokoju trafiają prosto do zespołu, bez telefonu i bez kartek.',
          x: x + inset,
          y: y + inset + Math.round(u * 0.09),
          width: width - inset * 2,
          fontSize: Math.round(u * 0.026),
          fontFamily: fonts.body,
          fontWeight: 400,
          lineHeight: 1.45,
          color: '#dbeae6',
        }),
      ];
    },
  },

  {
    id: 'quote',
    label: 'Cytat z awatarem',
    description: 'Zdjęcie w kole, wypowiedź i podpis',
    icon: Quote,
    build: ({ scene, palette, fonts }) => {
      const u = unit(scene);
      const x = margin(scene);
      const width = scene.width - x * 2;
      const y = Math.round(scene.height * 0.42);
      const avatar = Math.round(u * 0.16);

      return [
        {
          ...base('Awatar'),
          type: 'image',
          src: '',
          x,
          y,
          width: avatar,
          height: avatar,
          radius: 0,
          fit: 'cover',
          circle: true,
        },
        text('Cytat', {
          text: '"Recepcja odbiera o połowę mniej telefonów."',
          x: x + avatar + Math.round(u * 0.04),
          y,
          width: width - avatar - Math.round(u * 0.04),
          fontSize: Math.round(u * 0.038),
          fontFamily: fonts.subtitle,
          fontWeight: 400,
          fontStyle: 'italic',
          lineHeight: 1.35,
          color: palette.light,
        }),
        text('Podpis', {
          text: 'Dyrektor obiektu, hotel 4 gwiazdki',
          x: x + avatar + Math.round(u * 0.04),
          y: y + Math.round(u * 0.12),
          width: width - avatar - Math.round(u * 0.04),
          fontSize: Math.round(u * 0.024),
          fontFamily: fonts.body,
          fontWeight: 600,
          uppercase: true,
          letterSpacing: Math.round(u * 0.004),
          color: palette.accent,
        }),
      ];
    },
  },

  {
    id: 'metric',
    label: 'Metryka',
    description: 'Duża liczba z podpisem',
    icon: TrendingUp,
    build: ({ scene, palette, fonts }) => {
      const u = unit(scene);
      const x = margin(scene);
      const width = Math.round((scene.width - x * 2) * 0.6);
      const y = Math.round(scene.height * 0.3);
      const fontSize = Math.round(u * 0.16);

      return [
        text('Liczba', {
          text: '+31%',
          x,
          y,
          width,
          fontSize,
          fontFamily: fonts.headline,
          fontWeight: 900,
          lineHeight: 1,
          color: palette.accent,
        }),
        text('Podpis liczby', {
          text: 'więcej zamówień room service po wdrożeniu',
          x,
          y: y + Math.round(fontSize * 1.15),
          width,
          fontSize: Math.round(u * 0.028),
          fontFamily: fonts.body,
          fontWeight: 500,
          lineHeight: 1.4,
          color: palette.light,
        }),
      ];
    },
  },

  {
    id: 'frame',
    label: 'Ramka',
    description: 'Obrys wewnątrz planszy',
    icon: Frame,
    build: ({ scene, palette }) => {
      const inset = Math.round(scene.width * 0.05);

      return [
        shape('Ramka', {
          x: inset,
          y: inset,
          width: scene.width - inset * 2,
          height: scene.height - inset * 2,
          fill: '',
          stroke: palette.accent,
          strokeWidth: Math.max(2, Math.round(unit(scene) * 0.005)),
        }),
      ];
    },
  },
];
